import React from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Card, CardActionArea, Chip, Typography } from "@mui/material";

import BrokerAssetCounts from "../BrokerAssetCounts";
import { CLAIM_MARKER_COLOR, LAYERS } from "../constants";
import { formatMoney } from "../helpers";
import { brokerStatsById, claimsByMotor, claimsByProperty } from "../portfolioData";

function EntityCard({ entity, layer, language, onOpen }) {
  const { t } = useTranslation();

  const title =
    layer === LAYERS.broker
      ? entity.name
      : layer === LAYERS.property
        ? entity.address
        : entity.registration_number;

  const stats = layer === LAYERS.broker ? brokerStatsById[entity.id] : null;
  const subtitle =
    layer === LAYERS.broker
      ? `${entity.city} · ${entity.manager_name}`
      : layer === LAYERS.property
        ? `${t(`portfolioExplorer.enums.propertyType.${entity.property_type}`)} · ${entity.city}`
        : `${entity.make} ${entity.model} · ${t(`portfolioExplorer.enums.vehicleType.${entity.vehicle_type}`)} · ${entity.city}`;
  const hasClaim =
    layer === LAYERS.property
      ? (claimsByProperty[entity.id] || []).length > 0
      : layer === LAYERS.motor
        ? (claimsByMotor[entity.id] || []).length > 0
        : false;
  const summary =
    layer === LAYERS.broker
      ? `${t("portfolioExplorer.card.totalInsured")} ${formatMoney(stats.totalInsured, language)} · ${t("portfolioExplorer.fields.premium")} ${formatMoney(stats.totalPremium, language)}`
      : layer === LAYERS.property
        ? `${t("portfolioExplorer.card.totalInsured")} ${formatMoney(entity.total_insured_value, language)} · ${t("portfolioExplorer.fields.premium")} ${formatMoney(entity.premium, language)}`
        : `${t("portfolioExplorer.fields.vehicleValue")} ${formatMoney(entity.vehicle_value, language)} · ${t("portfolioExplorer.fields.premium")} ${formatMoney(entity.premium, language)}`;

  return (
    <Card variant="outlined" sx={{ mb: 1 }}>
      <CardActionArea onClick={() => onOpen(layer, entity.id)} sx={{ p: 1.25 }}>
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
          {layer === LAYERS.broker && (
            <Avatar src={entity.logo_url} alt="" sx={{ width: 40, height: 40 }} />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
                {title}
              </Typography>
              {hasClaim && (
                <Chip
                  size="small"
                  label={t("portfolioExplorer.claims.badge")}
                  sx={{
                    height: 20,
                    flexShrink: 0,
                    bgcolor: CLAIM_MARKER_COLOR,
                    color: "#fff",
                    "& .MuiChip-label": { px: 0.75, fontSize: 11, fontWeight: 600 },
                  }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" component="div">
              {subtitle}
            </Typography>
            {layer === LAYERS.broker && (
              <Typography variant="caption" color="text.secondary" component="div">
                <BrokerAssetCounts stats={stats} />
              </Typography>
            )}
            {layer === LAYERS.broker && (
              <Typography variant="caption" color="text.secondary" component="div">
                {t("portfolioExplorer.claims.count", { count: stats.claimCount })}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" component="div">
              {summary}
            </Typography>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
}

export default EntityCard;
